import { h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import type { MatchStoreState } from '../../../../../services/interfaces';
import { translateEntity } from '../../../../../services/utils';
import {
  getTeamServiceUrl,
  setCssVariant,
  showCountryCode,
  isNationsLeague,
  getBadgeFallback,
  isVerticalLayout,
} from '../../helpers/match-unit.helpers';

type Props = {
  isAwayTeam?: boolean;
  state: ObservableMap<MatchStoreState>['state'];
};

const MatchTeam = ({ state, isAwayTeam = false }: Props) => {
  const useTeamCode = state.useTeamCode;
  const size = state.size;
  const isVertical = isVerticalLayout(size);
  const cssVariant = setCssVariant(size);

  const disableLink = state.disableTeamsLink;
  const teamServiceUrl = state.teamServiceUrl;
  const match = state.matchData;
  const showRedCards = isVertical && state.showRedCards;
  if (!match) return null;

  const competition = match.competition;
  const round = match.round;
  const team = !isAwayTeam ? match.homeTeam : match.awayTeam;
  const redCards = match.playerEvents?.redCards?.filter((card) => card.teamId === team.id) ?? [];

  const isPlaceHolder = !team || team.isPlaceHolder;
  const teamUrl = !isPlaceHolder ? getTeamServiceUrl(teamServiceUrl, team.id, competition.id) : null;

  const logoUrl = isNationsLeague(competition?.id, round)
    ? team?.logoUrl?.replace(/\/\d+x\d+\//, '/unl/')
    : team?.logoUrl;

  const displayTeamName = () => {
    if (useTeamCode) {
      return translateEntity(team)?.displayTeamCode;
    }

    return ` ${translateEntity(team)?.displayName ?? ''} `;
  };

  const renderBadge = () => (
    <pk-badge
      class={{ 'team-badge': true, ...cssVariant }}
      alt={translateEntity(team)?.displayOfficialName ?? ''}
      badgeTitle={translateEntity(team)?.displayOfficialName ?? ''}
      fallbackImage={getBadgeFallback(team, competition, round)}
      src={!isPlaceHolder ? logoUrl : null}
      part={!isAwayTeam ? 'team-home-badge' : 'team-away-badge'}
    />
  );

  return (
    <div
      class={{
        'pk-match__base--team-home': !isAwayTeam,
        'pk-match__base--team-away': isAwayTeam,
        ...cssVariant,
      }}
      part={!isAwayTeam ? 'team-home' : 'team-away'}
    >
      {showRedCards && redCards.length > 0 && (
        <div class="red-card">
          <pk-icon class="red-card__icon" name="football-card-single-card" color="support-04A" />
          {redCards.length > 1 && <span class="red-card__count">{redCards.length}</span>}
        </div>
      )}
      <div class="team-name" part={!isAwayTeam ? 'team-home-name' : 'team-away-name'}>
        {displayTeamName()}
        {!useTeamCode && showCountryCode(team, round, competition?.teamCategory) && (
          <span class="country-code" part="country-code">{` (${team?.countryCode}) `}</span>
        )}
      </div>
      {!disableLink && teamUrl ? (
        <a class="flex" href={teamUrl} title={translateEntity(team)?.displayName ?? ''}>
          {renderBadge()}
        </a>
      ) : (
        renderBadge()
      )}
    </div>
  );
};

export { MatchTeam };
